import React, { useState, useEffect, useRef, useCallback } from "react";
import { Modal, Table, Pagination } from "react-bootstrap";
import { ModalSkeleton } from "../../../components/Dashboard/SkeletonLoaders";
import { getSkorProtectLandlordDetails } from "../../../context/functions/AdminFunction";
import { toast } from "react-toastify";
import moment from "moment";
import { X } from "lucide-react";
import { constants } from "../../../utils/constants";

const LIMIT = constants.LISTING_ROWLIMIT;

const PROTECT_STATUS_OPTIONS = [
  { value: "all", label: "All" },
  { value: "waiting_for_payment", label: "Waiting for Payment" },
  { value: "waiting_for_rental_contract", label: "Waiting for Contract" },
];

const formatProtectStatus = (status) => {
  if (!status) return "N/A";
  if (status === "waiting_for_payment") return "Waiting for Payment";
  if (status === "waiting_for_rental_contract") return "Waiting for Contract";
  return status.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

const formatPaymentStatus = (status) => {
  if (!status) return "N/A";
  return status.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

const SkorProtectDetailsModal = ({ show, onHide, selectedLandlord }) => {
  const [workspaceFilter, setWorkspaceFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [paymentFilter, setPaymentFilter] = useState("all");

  const [protectList, setProtectList] = useState([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const [workspaceOptions, setWorkspaceOptions] = useState([]);
  const [paymentStatusOptions, setPaymentStatusOptions] = useState([]);

  const abortRef = useRef(null);

  const fetchDetails = useCallback(
    async ({ workspaceId, skor_protect_status, payment_status, page }) => {
      const landlordId = selectedLandlord?.landlord_id || selectedLandlord?.landlord?._id;
      if (!landlordId) return;

      if (abortRef.current) abortRef.current.abort();
      abortRef.current = new AbortController();

      setLoading(true);
      try {
        const res = await getSkorProtectLandlordDetails({
          landlordId,
          workspaceId: workspaceId === "all" ? "" : workspaceId,
          skor_protect_status,
          payment_status: payment_status === "all" ? "" : payment_status,
          page,
          limit: LIMIT,
          signal: abortRef.current.signal,
        });

        if (!res || res.status === "canceled") return;

        if (res.success && res.success_res) {
          setProtectList(res.success_res.data || []);
          setTotal(res.success_res.total || 0);
          if (res.success_res.workspaces?.length) setWorkspaceOptions(res.success_res.workspaces);
          if (res.success_res.paymentStatuses?.length) setPaymentStatusOptions(res.success_res.paymentStatuses);
        } else {
          setProtectList([]);
          setTotal(0);
        }
      } catch (err) {
        if (err.name !== "CanceledError" && err.code !== "ERR_CANCELED") {
          toast.error("Failed to load Skor Protect details");
        }
      } finally {
        setLoading(false);
      }
    },
    [selectedLandlord]
  );

  useEffect(() => {
    if (!show) return;
    fetchDetails({
      workspaceId: workspaceFilter,
      skor_protect_status: statusFilter,
      payment_status: paymentFilter,
      page: currentPage,
    });
  }, [show, workspaceFilter, statusFilter, paymentFilter, currentPage, fetchDetails]);

  useEffect(() => {
    if (show) {
      setWorkspaceFilter("all");
      setStatusFilter("all");
      setPaymentFilter("all");
      setCurrentPage(1);
      setProtectList([]);
      setWorkspaceOptions([]);
      setPaymentStatusOptions([]);
    }
  }, [show, selectedLandlord?.landlord_id]);

  useEffect(() => {
    return () => { if (abortRef.current) abortRef.current.abort(); };
  }, []);

  const handleFilterChange = (type, value) => {
    setCurrentPage(1);
    if (type === "workspace") setWorkspaceFilter(value);
    if (type === "status") setStatusFilter(value);
    if (type === "payment") setPaymentFilter(value);
  };

  const handleHide = () => {
    if (abortRef.current) abortRef.current.abort();
    onHide();
  };

  const totalPages = Math.ceil(total / LIMIT);

  const renderPagination = () => {
    const items = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }
    items.push(<Pagination.First key="first" onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />);
    items.push(<Pagination.Prev key="prev" onClick={() => setCurrentPage((p) => p - 1)} disabled={currentPage === 1} />);
    for (let p = startPage; p <= endPage; p++) {
      items.push(
        <Pagination.Item key={p} active={p === currentPage} onClick={() => setCurrentPage(p)}>{p}</Pagination.Item>
      );
    }
    items.push(<Pagination.Next key="next" onClick={() => setCurrentPage((p) => p + 1)} disabled={currentPage === totalPages || totalPages === 0} />);
    items.push(<Pagination.Last key="last" onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages || totalPages === 0} />);

    return (
      <div className="d-flex justify-content-between align-items-center mt-4">
        <div className="text-muted small">
          Showing <strong>{total > 0 ? (currentPage - 1) * LIMIT + 1 : 0}</strong> to{" "}
          <strong>{Math.min(currentPage * LIMIT, total)}</strong> of{" "}
          <strong>{total}</strong> records
        </div>
        <Pagination className="tenants-pagination mb-0">{items}</Pagination>
      </div>
    );
  };

  const formatProperty = (property) => {
    if (!property) return "N/A";
    return [property.street, property.street_number, property.muncipality]
      .filter(Boolean)
      .join(", ") || "N/A";
  };

  const getProtectStatusBadgeClass = (status) => {
    if (status === "waiting_for_payment") return "status-badge-expired";
    if (status === "waiting_for_rental_contract") return "insurance-payment-badge direct-debit";
    return "status-badge-active";
  };

  const getPaymentStatusBadgeClass = (status) => {
    if (!status) return "";
    if (status === "paid") return "status-badge-active";
    if (status === "pending" || status === "waiting") return "insurance-payment-badge direct-debit";
    return "insurance-payment-badge card";
  };

  return (
    <Modal
      show={show}
      onHide={handleHide}
      size="xl"
      dialogClassName="modal-90w"
      centered
      scrollable
      className="landlord-metrics-modal"
    >
      <Modal.Header className="landlord-details-header">
        <div className="d-flex justify-content-between align-items-start w-100">
          <div>
            <h4 className="landlord-details-name">
              {selectedLandlord?.landlord?.fname_companyName}{" "}
              {selectedLandlord?.landlord?.lname}
            </h4>
            <p className="landlord-details-email">
              {selectedLandlord?.landlord?.email || "No email provided"}
            </p>
          </div>
          <button className="modal-close-btn" onClick={handleHide}>
            <X size={20} />
          </button>
        </div>
      </Modal.Header>

      <Modal.Body className="px-5 pb-5 pt-0">
        {/* Workspace Selector */}
        <div className="workspace-select-container mt-4 mb-4">
          <select
            className="workspace-select"
            value={workspaceFilter}
            onChange={(e) => handleFilterChange("workspace", e.target.value)}
          >
            <option value="all">All workspaces</option>
            {workspaceOptions.map((ws) => (
              <option key={ws._id} value={ws._id}>{ws.name}</option>
            ))}
          </select>
        </div>

        {/* Filter Pills */}
        <div className="filter-pills-row mb-4">
          <div className="filter-pill-group">
            <span className="filter-pill-label">Status</span>
            <div className="filter-pill-options">
              {PROTECT_STATUS_OPTIONS.map((opt) => (
                <div
                  key={opt.value}
                  className={`modal-filter-pill ${statusFilter === opt.value ? "active" : ""}`}
                  onClick={() => handleFilterChange("status", opt.value)}
                >
                  {opt.label}
                </div>
              ))}
            </div>
          </div>

          {paymentStatusOptions.length > 0 && (
            <div className="filter-pill-group">
              <span className="filter-pill-label">Payment</span>
              <div className="filter-pill-options">
                <div
                  className={`modal-filter-pill ${paymentFilter === "all" ? "active" : ""}`}
                  onClick={() => handleFilterChange("payment", "all")}
                >
                  All
                </div>
                {paymentStatusOptions.map((status) => (
                  <div
                    key={status}
                    className={`modal-filter-pill ${paymentFilter === status ? "active" : ""}`}
                    onClick={() => handleFilterChange("payment", status)}
                  >
                    {formatPaymentStatus(status)}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Table */}
        {loading ? (
          <ModalSkeleton />
        ) : (
          <>
            <div className="table-responsive">
              <Table hover className="landlord-modal-table mb-0">
                <thead>
                  <tr>
                    <th>PROPERTY</th>
                    <th>TENANTS</th>
                    <th>WORKSPACES</th>
                    <th>INITIATED</th>
                    <th>AMOUNT</th>
                    <th>PAYMENT STATUS</th>
                    <th>PROTECT STATUS</th>
                  </tr>
                </thead>
                <tbody>
                  {protectList.length > 0 ? (
                    protectList.map((item, index) => {
                      const tenantNames = (item.tenants || [])
                        .map((t) => `${t.fname || ""} ${t.lname || ""}`.trim())
                        .filter(Boolean)
                        .join(", ");

                      const workspaceNames = (item.workspaces || (item.workspace ? [item.workspace] : []))
                        .map((ws) => ws.name)
                        .filter(Boolean)
                        .join(", ");

                      return (
                        <tr key={item._id || index}>
                          <td className="fw-600">{formatProperty(item.property)}</td>
                          <td style={{ maxWidth: "140px" }}>
                            <div
                              className="fw-600"
                              style={{ fontSize: "0.8rem", lineHeight: "1.3", wordBreak: "break-word" }}
                            >
                              {tenantNames || "N/A"}
                            </div>
                          </td>
                          <td style={{ maxWidth: "140px" }}>
                            <div
                              className="fw-600"
                              style={{ fontSize: "0.8rem", lineHeight: "1.3", wordBreak: "break-word" }}
                            >
                              {workspaceNames || "N/A"}
                            </div>
                          </td>
                          <td className="text-muted small fw-medium">
                            {item.skor_protect_initiated
                              ? moment(item.skor_protect_initiated).format("DD MMM YYYY")
                              : "N/A"}
                          </td>
                          <td className="fw-600">
                            {item.payment_amount ? `€${item.payment_amount}` : "N/A"}
                          </td>
                          <td>
                            {item.payment_status ? (
                              <span className={getPaymentStatusBadgeClass(item.payment_status)}>
                                {formatPaymentStatus(item.payment_status)}
                              </span>
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </td>
                          <td>
                            {item.property?.is_awarded ? (
                              <span className="status-badge-active">Awarded</span>
                            ) : (
                              <span className={getProtectStatusBadgeClass(item.skor_protect_status)}>
                                {formatProtectStatus(item.skor_protect_status)}
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={7} className="text-center py-5 text-muted">
                        No results found matching your filters
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
            {totalPages > 1 && total > 0 && (
              <div className="px-1">{renderPagination()}</div>
            )}
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default SkorProtectDetailsModal;
